import { getErrorStatus } from '@modernloop/shared/utils';
import { AxiosError } from 'axios';

/** Stable error codes surfaced in attachment upload UI. */
export const ATTACHMENT_UPLOAD_ERROR_CODE = {
  NETWORK_ERROR: 'NETWORK_ERROR',
  UNAUTHORIZED: 'UNAUTHORIZED',
  BAD_REQUEST: 'BAD_REQUEST',
  FILE_TOO_LARGE: 'FILE_TOO_LARGE',
  SERVER_ERROR: 'SERVER_ERROR',
  UNKNOWN: 'UNKNOWN',
} as const;

export type AttachmentUploadErrorCode =
  (typeof ATTACHMENT_UPLOAD_ERROR_CODE)[keyof typeof ATTACHMENT_UPLOAD_ERROR_CODE];

type AttachmentUploadError = {
  errorCode: AttachmentUploadErrorCode | string;
};

const isAxiosError = (error: unknown): error is AxiosError =>
  typeof error === 'object' && error !== null && 'isAxiosError' in error && Boolean(error.isAxiosError);

const isRecord = (value: unknown): value is Record<string, unknown> => typeof value === 'object' && value !== null;

const getApiErrorReason = (data: unknown): string | undefined => {
  if (!isRecord(data)) {
    return undefined;
  }

  const details = data.details;
  if (!Array.isArray(details) || details.length === 0) {
    return undefined;
  }

  const firstDetail = details[0];
  if (!isRecord(firstDetail)) {
    return undefined;
  }

  const reason = firstDetail.reason;
  return typeof reason === 'string' && reason.length > 0 ? reason : undefined;
};

/**
 * Maps upload API failures to stable error codes for user-facing attachment errors.
 */
export function getAttachmentUploadError(error: unknown): AttachmentUploadError {
  if (!isAxiosError(error)) {
    return { errorCode: ATTACHMENT_UPLOAD_ERROR_CODE.UNKNOWN };
  }

  const status = getErrorStatus(error);
  const reason = getApiErrorReason(error.response?.data);
  if (reason) {
    return { errorCode: reason };
  }

  if (!error.response) {
    return { errorCode: ATTACHMENT_UPLOAD_ERROR_CODE.NETWORK_ERROR };
  }

  if (status === 401) {
    return { errorCode: ATTACHMENT_UPLOAD_ERROR_CODE.UNAUTHORIZED };
  }

  if (status === 400) {
    return { errorCode: ATTACHMENT_UPLOAD_ERROR_CODE.BAD_REQUEST };
  }

  if (status === 413) {
    return { errorCode: ATTACHMENT_UPLOAD_ERROR_CODE.FILE_TOO_LARGE };
  }

  if (status >= 500) {
    return { errorCode: ATTACHMENT_UPLOAD_ERROR_CODE.SERVER_ERROR };
  }

  return { errorCode: ATTACHMENT_UPLOAD_ERROR_CODE.UNKNOWN };
}
